import React, { useState, useEffect } from 'react';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import NavigationBreadcrumbs from '../../components/navigation/NavigationBreadcrumbs';
import WellnessScoreCard from './components/WellnessScoreCard';
import RiskSegmentationChart from './components/RiskSegmentationChart';
import FinancialStressDistribution from './components/FinancialStressDistribution';
import EmployeeImprovementTracker from './components/EmployeeImprovementTracker';
import BatchEnrollmentInterface from './components/BatchEnrollmentInterface';
import EducationModulesManager from './components/EducationModulesManager';
import RiskHeatmapVisualization from './components/RiskHeatmapVisualization';
import OrganizationReporting from './components/OrganizationReporting';
import Icon from '../../components/AppIcon';
import EnterpriseAIRecommendationsPanel from './components/EnterpriseAIRecommendationsPanel';

import ARModeViewer from '../../components/ARModeViewer';
import { useAuth } from '../../contexts/AuthContext';
import { enterpriseService } from '../../services/financialDataService';
import { supabase } from '../../lib/supabase';

const EnterpriseClientDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [showARMode, setShowARMode] = useState(false);
  const [loading, setLoading] = useState(true);

  const [riskSegmentationData, setRiskSegmentationData] = useState([
    { name: 'Low Risk', value: 542, percentage: 43.5 },
    { name: 'Medium Risk', value: 438, percentage: 35.1 },
    { name: 'High Risk', value: 187, percentage: 15.0 },
    { name: 'Critical', value: 80, percentage: 6.4 }
  ]);

  const [stressDistributionData] = useState([
    { month: 'Aug', low: 520, medium: 450, high: 200 },
    { month: 'Sep', low: 535, medium: 440, high: 195 },
    { month: 'Oct', low: 548, medium: 435, high: 185 },
    { month: 'Nov', low: 555, medium: 430, high: 180 },
    { month: 'Dec', low: 560, medium: 425, high: 175 },
    { month: 'Jan', low: 542, medium: 438, high: 187 }
  ]);

  const [improvementData] = useState([
    { month: 'Aug', improvement: 62, engagement: 58 },
    { month: 'Sep', improvement: 65, engagement: 61 },
    { month: 'Oct', improvement: 68, engagement: 64 },
    { month: 'Nov', improvement: 71, engagement: 67 },
    { month: 'Dec', improvement: 74, engagement: 70 },
    { month: 'Jan', improvement: 76, engagement: 73 }
  ]);

  const [improvementMetrics] = useState([
    { label: 'Improved', value: '76%', trend: 4, icon: 'TrendingUp' },
    { label: 'Engaged', value: '73%', trend: 3, icon: 'Users' },
    { label: 'Active Users', value: '1,089', trend: 8, icon: 'Activity' },
    { label: 'Avg. Sessions', value: '12.4', trend: 2, icon: 'BarChart3' }
  ]);

  const educationModules = [
    {
      id: 1,
      title: 'Debt Management Fundamentals',
      description: 'Strategies for reducing and managing personal debt effectively',
      icon: 'CreditCard',
      enrolled: 487,
      completed: 392,
      completionRate: 80,
      duration: '45 min',
      status: 'active'
    },
    {
      id: 2,
      title: 'Emergency Fund Building',
      description: 'Creating financial safety nets for unexpected expenses',
      icon: 'Shield',
      enrolled: 623,
      completed: 498,
      completionRate: 80,
      duration: '35 min',
      status: 'active'
    },
    {
      id: 3,
      title: 'Retirement Planning Basics',
      description: 'Understanding 401(k), IRA, and long-term savings strategies',
      icon: 'PiggyBank',
      enrolled: 412,
      completed: 247,
      completionRate: 60,
      duration: '60 min',
      status: 'active'
    },
    {
      id: 4,
      title: 'Investment Fundamentals',
      description: 'Introduction to stocks, bonds, and diversification principles',
      icon: 'TrendingUp',
      enrolled: 298,
      completed: 119,
      completionRate: 40,
      duration: '50 min',
      status: 'active'
    }
  ];

  const departmentRiskData = [
    {
      id: 1,
      name: 'Engineering',
      employees: 342,
      riskLevel: 3,
      highStress: 28,
      debtConcerns: 42,
      lowSavings: 67,
      avgScore: 72
    },
    {
      id: 2,
      name: 'Sales',
      employees: 218,
      riskLevel: 5,
      highStress: 45,
      debtConcerns: 58,
      lowSavings: 82,
      avgScore: 64
    },
    {
      id: 3,
      name: 'Marketing',
      employees: 156,
      riskLevel: 4,
      highStress: 32,
      debtConcerns: 38,
      lowSavings: 54,
      avgScore: 68
    },
    {
      id: 4,
      name: 'Operations',
      employees: 287,
      riskLevel: 6,
      highStress: 62,
      debtConcerns: 71,
      lowSavings: 98,
      avgScore: 58
    },
    {
      id: 5,
      name: 'Human Resources',
      employees: 89,
      riskLevel: 2,
      highStress: 12,
      debtConcerns: 18,
      lowSavings: 23,
      avgScore: 78
    },
    {
      id: 6,
      name: 'Finance',
      employees: 155,
      riskLevel: 2,
      highStress: 15,
      debtConcerns: 22,
      lowSavings: 31,
      avgScore: 76
    }
  ];

  const enterpriseARData = {
    totalEmployees: 1247,
    avgWellnessScore: 68,
    highRiskCount: 187,
    highRiskPercentage: 15.0,
    engagementRate: 73,
    improvementRate: 76
  };

  const [enterpriseClients, setEnterpriseClients] = useState([]);
  const [employees, setEmployees] = useState([]);

  // Fetch enterprise data from Supabase
  useEffect(() => {
    if (!user?.id) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch enterprise clients
        const clientsData = await enterpriseService?.getAll();
        setEnterpriseClients(clientsData);

        // Fetch employees for first client
        if (clientsData?.length > 0) {
          const employeesData = await enterpriseService?.getEmployees(clientsData?.[0]?.id);
          setEmployees(employeesData);

          // Calculate risk segmentation from employees
          const lowRisk = employeesData?.filter(e => e?.financialStressScore < 40)?.length;
          const mediumRisk = employeesData?.filter(e => e?.financialStressScore >= 40 && e?.financialStressScore < 60)?.length;
          const highRisk = employeesData?.filter(e => e?.financialStressScore >= 60 && e?.financialStressScore < 80)?.length;
          const critical = employeesData?.filter(e => e?.financialStressScore >= 80)?.length;
          const total = employeesData?.length;

          if (total > 0) {
            setRiskSegmentationData([
              { name: 'Low Risk', value: lowRisk, percentage: ((lowRisk / total) * 100)?.toFixed(1) },
              { name: 'Medium Risk', value: mediumRisk, percentage: ((mediumRisk / total) * 100)?.toFixed(1) },
              { name: 'High Risk', value: highRisk, percentage: ((highRisk / total) * 100)?.toFixed(1) },
              { name: 'Critical', value: critical, percentage: ((critical / total) * 100)?.toFixed(1) }
            ]);
          }
        }
      } catch (error) {
        console.error('Error fetching enterprise data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Real-time subscriptions
    const enterpriseChannel = supabase?.channel('enterprise_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'enterprise_clients'
        },
        () => {
          fetchData();
        }
      )?.subscribe();

    const employeesChannel = supabase?.channel('employees_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'enterprise_employees'
        },
        () => {
          fetchData();
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(enterpriseChannel);
      supabase?.removeChannel(employeesChannel);
    };
  }, [user?.id]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'enrollment', label: 'Enrollment', icon: 'UserPlus' },
    { id: 'education', label: 'Education', icon: 'BookOpen' },
    { id: 'reporting', label: 'Reporting', icon: 'FileText' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-fintech-purple-50 via-white to-fintech-blue-50">
      <RoleBasedNavigation role="enterprise-client" />
      <main className="pt-20 pb-8 px-4 md:px-6 lg:px-8">
        <div className="max-w-[1440px] mx-auto">
          <NavigationBreadcrumbs />

          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-fintech-purple-600 to-fintech-blue-600 bg-clip-text text-transparent mb-2">
              Enterprise Client Dashboard
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Comprehensive workforce financial wellness management and analytics
            </p>
          </div>

          <div className="mb-6 md:mb-8 overflow-x-auto">
            <div className="flex gap-2 min-w-max">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'bg-gradient-to-r from-fintech-purple-500 to-fintech-blue-500 text-white shadow-elevation-2'
                      : 'bg-white/70 backdrop-blur-sm text-muted-foreground hover:bg-fintech-purple-50 border border-fintech-purple-200'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'overview' && (
            <>
              {/* AR Feature Highlight for Enterprise */}
              <div className="mb-6 bg-gradient-to-br from-fintech-blue-500 via-fintech-purple-500 to-fintech-teal-500 rounded-3xl p-8 shadow-elevation-4 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-fintech-green-500/20 rounded-full blur-3xl"></div>
                <div className="relative z-10">
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center animate-float">
                        <Icon name="Camera" size={28} className="text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h2 className="text-2xl font-bold text-white">AR Financial Literacy</h2>
                          <span className="px-3 py-1 bg-fintech-green-500 text-white text-xs font-bold rounded-full animate-pulse">ENTERPRISE</span>
                        </div>
                        <p className="text-white/90 text-sm">Interactive employee engagement and risk visualization</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowARMode(true)}
                      className="px-6 py-3 bg-white text-fintech-purple-600 rounded-xl font-bold shadow-elevation-3 hover:shadow-elevation-5 hover:scale-105 transition-all duration-300 flex items-center gap-2"
                    >
                      <Icon name="Camera" size={20} />
                      Launch AR Mode
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                      <div className="flex items-center gap-3 mb-2">
                        <Icon name="Users" size={20} className="text-white" />
                        <h3 className="text-white font-semibold">Interactive Demos</h3>
                      </div>
                      <p className="text-white/80 text-sm">Financial literacy simulations for employee engagement</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                      <div className="flex items-center gap-3 mb-2">
                        <Icon name="AlertTriangle" size={20} className="text-white" />
                        <h3 className="text-white font-semibold">Risk Visualization</h3>
                      </div>
                      <p className="text-white/80 text-sm">AR-powered workshops for financial stress identification</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                      <div className="flex items-center gap-3 mb-2">
                        <Icon name="BookOpen" size={20} className="text-white" />
                        <h3 className="text-white font-semibold">Educational AR</h3>
                      </div>
                      <p className="text-white/80 text-sm">Immersive learning experiences for workforce wellness</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
                <WellnessScoreCard 
                  score={68}
                  trend={4}
                  previousScore={64}
                />
                <div className="lg:col-span-3">
                  <RiskSegmentationChart data={riskSegmentationData} />
                </div>
              </div>

              <div className="mb-6">
                <EnterpriseAIRecommendationsPanel
                  workforceData={{
                    totalEmployees: 1247,
                    avgWellnessScore: 68,
                    highRiskCount: 267,
                    highRiskPercentage: 21.4,
                    engagementRate: 73,
                    improvementRate: 76,
                    departments: departmentRiskData
                  }}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <FinancialStressDistribution data={stressDistributionData} />

                <EmployeeImprovementTracker 
                  data={improvementData} 
                  metrics={improvementMetrics}
                />

                <RiskHeatmapVisualization departments={departmentRiskData} />
              </div>
            </>
          )}

          {activeTab === 'enrollment' && (
            <div className="space-y-6 md:space-y-8">
              <BatchEnrollmentInterface />
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                <div className="bg-card rounded-xl p-4 md:p-6 shadow-elevation-2 border border-border">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 md:p-3 rounded-lg bg-success/10">
                      <Icon name="UserCheck" size={20} className="text-success" />
                    </div>
                    <div>
                      <p className="text-xs md:text-sm text-muted-foreground">Total Enrolled</p>
                      <p className="text-xl md:text-2xl font-bold text-foreground">1,247</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-success">
                    <Icon name="TrendingUp" size={14} />
                    <span>12% from last month</span>
                  </div>
                </div>

                <div className="bg-card rounded-xl p-4 md:p-6 shadow-elevation-2 border border-border">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 md:p-3 rounded-lg bg-warning/10">
                      <Icon name="Clock" size={20} className="text-warning" />
                    </div>
                    <div>
                      <p className="text-xs md:text-sm text-muted-foreground">Pending Activation</p>
                      <p className="text-xl md:text-2xl font-bold text-foreground">43</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Icon name="Minus" size={14} />
                    <span>Awaiting email verification</span>
                  </div>
                </div>

                <div className="bg-card rounded-xl p-4 md:p-6 shadow-elevation-2 border border-border">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 md:p-3 rounded-lg bg-primary/10">
                      <Icon name="Activity" size={20} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-xs md:text-sm text-muted-foreground">Active Users</p>
                      <p className="text-xl md:text-2xl font-bold text-foreground">1,089</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-success">
                    <Icon name="TrendingUp" size={14} />
                    <span>87% engagement rate</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'education' && (
            <div className="space-y-6 md:space-y-8">
              <EducationModulesManager modules={educationModules} />

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                <div className="bg-card rounded-xl p-4 md:p-6 shadow-elevation-2 border border-border">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Icon name="BookOpen" size={20} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Total Modules</p>
                      <p className="text-2xl font-bold text-foreground">12</p>
                    </div>
                  </div>
                </div>

                <div className="bg-card rounded-xl p-4 md:p-6 shadow-elevation-2 border border-border">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-success/10">
                      <Icon name="CheckCircle" size={20} className="text-success" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Completed</p>
                      <p className="text-2xl font-bold text-foreground">1,256</p>
                    </div>
                  </div>
                </div>

                <div className="bg-card rounded-xl p-4 md:p-6 shadow-elevation-2 border border-border">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-warning/10">
                      <Icon name="Clock" size={20} className="text-warning" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">In Progress</p>
                      <p className="text-2xl font-bold text-foreground">487</p>
                    </div>
                  </div>
                </div>

                <div className="bg-card rounded-xl p-4 md:p-6 shadow-elevation-2 border border-border">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Icon name="Target" size={20} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Avg. Completion</p>
                      <p className="text-2xl font-bold text-foreground">65%</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'reporting' && (
            <div className="space-y-6 md:space-y-8">
              <OrganizationReporting />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                <div className="bg-card rounded-xl p-4 md:p-6 lg:p-8 shadow-elevation-2 border border-border">
                  <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">
                    Quick Stats
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <span className="text-sm text-foreground">Reports Generated</span>
                      <span className="text-sm font-semibold text-foreground">47</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <span className="text-sm text-foreground">Scheduled Reports</span>
                      <span className="text-sm font-semibold text-foreground">8</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <span className="text-sm text-foreground">Last Generated</span>
                      <span className="text-sm font-semibold text-foreground">2 hours ago</span>
                    </div>
                  </div>
                </div>

                <div className="bg-card rounded-xl p-4 md:p-6 lg:p-8 shadow-elevation-2 border border-border">
                  <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">
                    Industry Benchmarks
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">Your Organization</span>
                        <span className="text-sm font-semibold text-foreground">72/100</span>
                      </div>
                      <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-primary rounded-full" style={{ width: '72%' }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">Industry Average</span>
                        <span className="text-sm font-semibold text-foreground">68/100</span>
                      </div>
                      <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-muted-foreground rounded-full" style={{ width: '68%' }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">Top Performers</span>
                        <span className="text-sm font-semibold text-foreground">85/100</span>
                      </div>
                      <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-success rounded-full" style={{ width: '85%' }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      {showARMode && (
        <ARModeViewer
          budgetData={[]}
          financialData={enterpriseARData}
          onClose={() => setShowARMode(false)}
          userRole="enterprise"
        />
      )}
    </div>
  );
};

export default EnterpriseClientDashboard;